import {
  IChargeUnit,
  IMonthNum,
  ISeasonMapping,
  ISeasonalPVCharge,
  ITimeDataItem,
  ITouPeriods,
} from './algoInterface'
import { calLoadTimeSum, calMonthSum } from './classifyTimeData'
import {
  calKepcoMonthlyChargeAsUsage,
  calMonthlyChargeAsUsage,
  sumMonthNumToTotalNum,
} from './calCharge'
import { calMonthlyExCharge, IMonthNumPlusIMonthNum } from './calExCharge'

const EMPTY_MONTH_NUM: IMonthNum = {
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 0,
  6: 0,
  7: 0,
  8: 0,
  9: 0,
  10: 0,
  11: 0,
  12: 0,
}

export const sumBuyCostGeneration = (buyCost: ISeasonalPVCharge): number =>
  buyCost.spr.pv + buyCost.sum.pv + buyCost.fall.pv + buyCost.win.pv

export const sumBuyCostCharge = (buyCost: ISeasonalPVCharge): number =>
  buyCost.spr.charge +
  buyCost.sum.charge +
  buyCost.fall.charge +
  buyCost.win.charge

export const spreadDeductionEvenlyUntilZero = (
  peakLoads: number[],
  targetKwh: number
): number[] => {
  const totalPeakLoad = peakLoads.reduce(
    (sum, load) => sum + Math.max(load, 0),
    0
  )
  let remaining = Math.min(Math.max(targetKwh, 0), totalPeakLoad)
  const deductions = peakLoads.map(() => 0)
  let activeIndexes = peakLoads
    .map((load, index) => ({ load, index }))
    .filter(({ load }) => load > 0)
    .map(({ index }) => index)

  while (remaining > 0.0000001 && activeIndexes.length > 0) {
    const share = remaining / activeIndexes.length
    let deductedThisRound = 0
    const nextActiveIndexes: number[] = []

    activeIndexes.forEach((index) => {
      const capacity = Math.max(peakLoads[index] - deductions[index], 0)
      const deduction = Math.min(capacity, share)

      deductions[index] += deduction
      deductedThisRound += deduction

      if (capacity - deduction > 0.0000001) {
        nextActiveIndexes.push(index)
      }
    })

    if (deductedThisRound <= 0.0000001) {
      break
    }

    remaining -= deductedThisRound
    activeIndexes = nextActiveIndexes
  }

  return deductions
}

export const buildPeakDeductionLoadData = (params: {
  loadData: ITimeDataItem[]
  totalPvGeneration: number
  seasonMapping: ISeasonMapping
  holidayData: number[]
  touPeriods: ITouPeriods
}): {
  essLoadData: ITimeDataItem[]
  kepcoLoadData: ITimeDataItem[]
  actualDeductedKwh: number
  unusedPvKwh: number
} => {
  const classifiedData = calLoadTimeSum(
    params.loadData,
    params.seasonMapping,
    params.holidayData,
    params.touPeriods
  ).timeDataResult
  const peakLoads = classifiedData
    .filter((item) => item.loadTime === 'max')
    .map((item) => item.value || 0)
  const deductions = spreadDeductionEvenlyUntilZero(
    peakLoads,
    params.totalPvGeneration
  )
  const actualDeductedKwh = deductions.reduce((sum, value) => sum + value, 0)
  let peakIndex = 0

  const essLoadData = classifiedData.map((item) => {
    const deduction = item.loadTime === 'max' ? deductions[peakIndex++] || 0 : 0

    return {
      ...item,
      value: deduction,
    }
  })

  peakIndex = 0
  const kepcoLoadData = classifiedData.map((item) => {
    const deduction = item.loadTime === 'max' ? deductions[peakIndex++] || 0 : 0

    return {
      ...item,
      value: Math.max((item.value || 0) - deduction, 0),
    }
  })

  return {
    essLoadData,
    kepcoLoadData,
    actualDeductedKwh,
    unusedPvKwh: Math.max(params.totalPvGeneration - actualDeductedKwh, 0),
  }
}

export const calPeakDeductionRevenueTarget = (params: {
  beforeCustomerCharge: number
  afterKepcoOnlyCustomerCharge: number
  pvPurchaseCost: number
  networkCost: number
  essProfitRate: number
}): {
  maxCustomerPayment: number
  minSellerCost: number
  profitPool: number
  targetEssRevenue: number
  isFeasible: boolean
} => {
  const maxCustomerPayment =
    params.beforeCustomerCharge - params.afterKepcoOnlyCustomerCharge
  const minSellerCost = params.pvPurchaseCost + params.networkCost
  const profitPool = maxCustomerPayment - minSellerCost
  const isFeasible = profitPool >= 0
  const targetEssRevenue = isFeasible
    ? minSellerCost + profitPool * (params.essProfitRate / 100)
    : 0

  return {
    maxCustomerPayment,
    minSellerCost,
    profitPool,
    targetEssRevenue,
    isFeasible,
  }
}

export const calPeakSingleChargeUnit = (params: {
  targetEssRevenue: number
  actualDeductedKwh: number
}): IChargeUnit => {
  const unit =
    params.actualDeductedKwh === 0
      ? 0
      : Number((params.targetEssRevenue / params.actualDeductedKwh).toFixed(2))

  return {
    spr: { min: 0, mid: 0, max: unit },
    sum: { min: 0, mid: 0, max: unit },
    fall: { min: 0, mid: 0, max: unit },
    win: { min: 0, mid: 0, max: unit },
  }
}

export const calPeakDeductionSellPlan = (params: {
  loadData: ITimeDataItem[]
  buyCost: ISeasonalPVCharge
  touPeriods: ITouPeriods
  kepcoChargeUnit: IChargeUnit
  defaultChargeUnit: number
  environmentalUnit?: number
  contractedPower: number
  seasonMapping: ISeasonMapping
  holidayData: number[]
  networkUnit: number
  essProfitRate: number
  greenPremiumUnit?: number
  re100Opt?: string
}): {
  chargeUnitResult: IChargeUnit
  essLoadData: ITimeDataItem[]
  kepcoLoadData: ITimeDataItem[]
  beforeKepcoCharge: IMonthNum
  afterKepcoCharge: IMonthNum
  afterEssCharge: IMonthNum
  beforeCustomerCharge: IMonthNum
  afterKepcoOnlyCustomerCharge: IMonthNum
  remainingGreenPremiumCharge: IMonthNum
  actualDeductedKwh: number
  unusedPvKwh: number
  maxCustomerPayment: number
  minSellerCost: number
  networkCost: number
  profitPool: number
  targetEssRevenue: number
  isFeasible: boolean
} => {
  const splitLoadData = buildPeakDeductionLoadData({
    loadData: params.loadData,
    totalPvGeneration: sumBuyCostGeneration(params.buyCost),
    seasonMapping: params.seasonMapping,
    holidayData: params.holidayData,
    touPeriods: params.touPeriods,
  })

  const beforeKepcoCharge = calKepcoMonthlyChargeAsUsage(
    params.loadData,
    params.touPeriods,
    params.kepcoChargeUnit,
    params.seasonMapping,
    params.holidayData,
    params.defaultChargeUnit,
    params.contractedPower,
    params.environmentalUnit ?? 0
  )
  const afterKepcoCharge = calKepcoMonthlyChargeAsUsage(
    splitLoadData.kepcoLoadData,
    params.touPeriods,
    params.kepcoChargeUnit,
    params.seasonMapping,
    params.holidayData,
    params.defaultChargeUnit,
    params.contractedPower,
    params.environmentalUnit ?? 0
  )
  const networkCost = splitLoadData.actualDeductedKwh * params.networkUnit
  const beforeGreenPremiumCharge =
    params.re100Opt === 'achieve'
      ? calMonthlyExCharge(
          params.greenPremiumUnit ?? 0,
          calMonthSum(params.loadData)
        )
      : EMPTY_MONTH_NUM
  const remainingGreenPremiumCharge =
    params.re100Opt === 'achieve'
      ? calMonthlyExCharge(
          params.greenPremiumUnit ?? 0,
          calMonthSum(splitLoadData.kepcoLoadData)
        )
      : EMPTY_MONTH_NUM
  const beforeCustomerCharge = IMonthNumPlusIMonthNum(
    beforeKepcoCharge,
    beforeGreenPremiumCharge
  )
  const afterKepcoOnlyCustomerCharge = IMonthNumPlusIMonthNum(
    afterKepcoCharge,
    remainingGreenPremiumCharge
  )
  const revenueTarget = calPeakDeductionRevenueTarget({
    beforeCustomerCharge: sumMonthNumToTotalNum(beforeCustomerCharge),
    afterKepcoOnlyCustomerCharge: sumMonthNumToTotalNum(
      afterKepcoOnlyCustomerCharge
    ),
    pvPurchaseCost: sumBuyCostCharge(params.buyCost),
    networkCost,
    essProfitRate: params.essProfitRate,
  })
  const chargeUnitResult = calPeakSingleChargeUnit({
    targetEssRevenue: revenueTarget.targetEssRevenue,
    actualDeductedKwh: splitLoadData.actualDeductedKwh,
  })
  const afterEssCharge = revenueTarget.isFeasible
    ? calMonthlyChargeAsUsage(
        splitLoadData.essLoadData,
        params.touPeriods,
        chargeUnitResult,
        params.seasonMapping,
        params.holidayData
      )
    : EMPTY_MONTH_NUM

  return {
    chargeUnitResult,
    essLoadData: splitLoadData.essLoadData,
    kepcoLoadData: splitLoadData.kepcoLoadData,
    beforeKepcoCharge,
    afterKepcoCharge,
    afterEssCharge,
    beforeCustomerCharge,
    afterKepcoOnlyCustomerCharge,
    remainingGreenPremiumCharge,
    actualDeductedKwh: splitLoadData.actualDeductedKwh,
    unusedPvKwh: splitLoadData.unusedPvKwh,
    maxCustomerPayment: revenueTarget.maxCustomerPayment,
    minSellerCost: revenueTarget.minSellerCost,
    networkCost,
    profitPool: revenueTarget.profitPool,
    targetEssRevenue: revenueTarget.targetEssRevenue,
    isFeasible: revenueTarget.isFeasible,
  }
}
